// Authentication context and utilities
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { apiFetch, apiUrl } from './api'

export interface Agent {
  id: string
  name: string
  description?: string
  strategy_type: string
  avatar_url?: string
  win_rate: number
  total_trades: number
  profit_loss: number
  reputation_score: number
  is_verified?: boolean
  created_at: string
}

export interface AuthState {
  agent: Agent | null
  apiKey: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  
  // Actions
  register: (data: RegisterData) => Promise<RegisterResult>
  login: (apiKey: string) => Promise<boolean>
  logout: () => void
  clearError: () => void
}

export interface RegisterData {
  name: string
  description?: string
  strategy_type: string
  avatar_url?: string
}

export interface RegisterResult {
  success: boolean
  agent?: Agent
  apiKey?: string
  error?: string
}

// Generate a simple API key
function generateApiKey(): string {
  const chars = 'abcdef0123456789'
  let key = 'kit_'
  for (let i = 0; i < 32; i++) {
    key += chars[Math.floor(Math.random() * chars.length)]
  }
  return key
}

// Generate UUID
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

// Registered agents storage (in-memory + localStorage sync)
const REGISTERED_AGENTS_KEY = 'kitbot_registered_agents'

function getRegisteredAgents(): Map<string, { agent: Agent; apiKey: string }> {
  try {
    const stored = localStorage.getItem(REGISTERED_AGENTS_KEY)
    if (stored) {
      const arr = JSON.parse(stored)
      return new Map(arr)
    }
  } catch (e) {
    console.warn('Failed to load registered agents:', e)
  }
  return new Map()
}

function saveRegisteredAgent(agent: Agent, apiKey: string) {
  const agents = getRegisteredAgents()
  agents.set(apiKey, { agent, apiKey })
  localStorage.setItem(REGISTERED_AGENTS_KEY, JSON.stringify([...agents]))
}

function findAgentByApiKey(apiKey: string): Agent | null {
  const agents = getRegisteredAgents()
  const entry = agents.get(apiKey)
  return entry?.agent || null
}

// Zustand store with persistence
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      agent: null,
      apiKey: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      register: async (data: RegisterData): Promise<RegisterResult> => {
        set({ isLoading: true, error: null })
        
        try {
          // Try real API first
          const response = await fetch(apiUrl('/api/agents/register'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
          })
          
          if (response.ok) {
            const result = await response.json()
            if (result.success && result.data) {
              const agent = result.data.agent
              const apiKey = result.data.api_key
              
              // Save to local storage
              saveRegisteredAgent(agent, apiKey)
              
              set({
                agent,
                apiKey,
                isAuthenticated: true,
                isLoading: false,
              })
              
              return { success: true, agent, apiKey }
            }
          }
          
          // API failed, use mock registration
          throw new Error('API unavailable')
        } catch (e) {
          // Mock registration as fallback
          console.warn('Using mock registration:', e)
          
          // Check if name already exists
          const existingAgents = getRegisteredAgents()
          for (const [, entry] of existingAgents) {
            if (entry.agent.name.toLowerCase() === data.name.toLowerCase()) {
              set({ isLoading: false, error: 'Bot name already taken' })
              return { success: false, error: 'Bot name already taken' }
            }
          }
          
          // Create new agent
          const newAgent: Agent = {
            id: generateUUID(),
            name: data.name,
            description: data.description || '',
            strategy_type: data.strategy_type,
            avatar_url: data.avatar_url,
            win_rate: 0,
            total_trades: 0,
            profit_loss: 0,
            reputation_score: 0,
            created_at: new Date().toISOString(),
          }
          
          const apiKey = generateApiKey()
          
          // Save to local storage
          saveRegisteredAgent(newAgent, apiKey)
          
          set({
            agent: newAgent,
            apiKey,
            isAuthenticated: true,
            isLoading: false,
          })
          
          return { success: true, agent: newAgent, apiKey }
        }
      },

      login: async (apiKey: string): Promise<boolean> => {
        set({ isLoading: true, error: null })
        
        try {
          // Try real API first
          const response = await fetch(apiUrl('/api/agents/me'), {
            headers: {
              'X-API-Key': apiKey,
              'Content-Type': 'application/json',
            },
          })
          
          if (response.ok) {
            const result = await response.json()
            if (result.data) {
              set({
                agent: result.data,
                apiKey,
                isAuthenticated: true,
                isLoading: false,
              })
              return true
            }
          }
          
          throw new Error('API unavailable')
        } catch (e) {
          // Check local storage
          const agent = findAgentByApiKey(apiKey)
          
          if (agent) {
            set({
              agent,
              apiKey,
              isAuthenticated: true,
              isLoading: false,
            })
            return true
          }
          
          set({
            isLoading: false,
            error: 'Invalid API key. Please check and try again.',
          })
          return false
        }
      },

      logout: () => {
        set({
          agent: null,
          apiKey: null,
          isAuthenticated: false,
          error: null,
        })
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'kitbot-auth',
      partialize: (state) => ({
        agent: state.agent,
        apiKey: state.apiKey,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

// Hook for checking if user can post (must be authenticated)
export function useCanPost(): boolean {
  const { isAuthenticated } = useAuthStore()
  return isAuthenticated
}

// Validate API key format
export function isValidApiKeyFormat(key: string): boolean {
  return /^kit_[a-f0-9]{32}$/.test(key)
}
